import { CSSProperties, FC, memo, useCallback, useMemo } from "react";
import "./checkbox.scss";
import classNames from "classnames";
import { FormInputElement } from "@archetypes/form-element";
import { LabelInfo } from "@components/label-info";
import { FormError } from "@components/form-error";

interface CheckboxProps extends FormInputElement {
  checked?: boolean;
  type?: "box" | "switch";
  onChange?: (checked: boolean, value?: string) => void;
  disabled?: boolean;
  label?: string;
  name?: string;
  value?: string | number;
  containerClassName?: string;
  labelPlacement?: "right" | "center" | "middle";
  labelClass?: string;
  mode?: string;
  selectedColor?: string;
}

const CheckboxComponent: FC<CheckboxProps> = ({
  checked,
  onChange,
  type = "box",
  label,
  id,
  className,
  containerClassName = "",
  labelPlacement = "left",
  labelClass,
  mode,
  selectedColor,
  ...restProps
}) => {
  const themedStyle = useMemo<CSSProperties | undefined>(() => {
    if (!selectedColor) {
      return undefined;
    }

    return {
      "--checkbox-selected-color": selectedColor,
      // Idle borders take a light tint of the same colour so a toggle group
      // reads as one control instead of a themed segment in a grey box.
      "--checkbox-idle-border-color": `color-mix(in srgb, ${selectedColor} 35%, #ffffff)`,
    } as CSSProperties;
  }, [selectedColor]);

  return (
    <div
      className={`${type === "switch" ? "switch-box" : "check-box"} ${containerClassName}`}
      style={themedStyle}
    >
      <label htmlFor={id} className={`${labelPlacement} ${labelClass}`}>
        <span className={type === "switch" ? "switch-container" : "checkbox-content"}>
          <input
            type="checkbox"
            checked={checked ?? false}
            tabIndex={0}
            onChange={(e) => {
              onChange?.(e.target.checked, e.target.value);
            }}
            className={classNames(className, {
              switch: type === "switch",
            })}
            id={id}
            {...restProps}
          />
          {type !== "switch" && (
            <span className="checkbox">{mode === "toggle" ? label : null}</span>
          )}
        </span>
        {mode !== "toggle" ? (
          <span className={mode === "toggle" ? "label" : "value"}>{label}</span>
        ) : null}
      </label>
    </div>
  );
};

export const Checkbox = memo(CheckboxComponent);

interface CheckboxGroupComponentProps<T> {
  onChange?: (value: T[]) => void;
  value?: T[];
  options?: T[];
  getKey: (value: T) => string | number;
  getValue: (value: T) => string | number;
  getLabel: (value: T) => string;
  label?: string;
  name?: string;
  required?: boolean;
  errorMessage?: string;
  labelClassName?: string;
  optionClassName?: string;
  infoText?: string;
  type?: "box" | "switch";
  disabled?: boolean;
  mode?: "toggle" | "default";
  isOptionDisabled?: (option: T) => boolean;
  containerClassName?: string;
  varient?: string | "border" | "";
  selectedColor?: string;
}

const CheckboxGroupComponent = <T,>({
  onChange,
  value,
  options,
  getKey,
  getValue,
  getLabel,
  required,
  label,
  errorMessage,
  labelClassName,
  optionClassName,
  infoText,
  disabled,
  type,
  mode,
  containerClassName,
  isOptionDisabled,
  varient,
  selectedColor,
}: CheckboxGroupComponentProps<T>) => {
  const handleChange = useCallback(
    (checked: boolean, option: T) => {
      const payload = Array.isArray(value) ? value : [];

      if (checked) {
        onChange?.([...payload, option]);
      } else {
        onChange?.(payload.filter((item) => getValue(item) !== getValue(option)));
      }
    },
    [getValue, onChange, value],
  );

  const currentValue = useMemo(() => value?.map((item) => getValue(item)) ?? [], [getValue, value]);

  return (
    <div className={`checkbox-container checkbox-group text-black ${containerClassName}`}>
      <LabelInfo
        label={label}
        labelClassName={labelClassName}
        required={required}
        infoText={infoText}
      />
      <div
        className={`checkbox-group-wrap w-100 ${mode === "toggle" ? "flex" : "d-flex flex-wrap"}${
          varient ? ` ${varient}` : ""
        }`}
      >
        {options?.map((option) => {
          const optionValue = getValue(option);
          const isDisabled = isOptionDisabled ? isOptionDisabled?.(option) : false;
          return (
            <Checkbox
              disabled={disabled || isDisabled}
              key={getKey(option)}
              value={optionValue}
              label={getLabel(option)}
              containerClassName={`question-checkbox ${
                mode === "toggle" ? "toggle" : ""
              } ${optionClassName}`}
              checked={currentValue.includes(optionValue)}
              onChange={(checked) => handleChange(checked, option)}
              type={type}
              mode={mode}
              selectedColor={selectedColor}
            />
          );
        })}
      </div>

      <FormError errorMessage={errorMessage} />
    </div>
  );
};

export const CheckboxGroup = memo(CheckboxGroupComponent) as typeof CheckboxGroupComponent;
